// Контракт кнопок блока (gamified-info-block-buttons.md). Всё, что студия знает про
// кнопки, живёт здесь: отдельных ручек add/update/delete нет — список целиком уезжает
// в PUT блока, поэтому важно, чтобы форма писала ровно то, что бек ждёт.
import type { BlockButton, BlockType } from '@/types/blocks'
import type { ContentLang } from '../editorSlice'

/** Единственный тип, который создаёт и показывает студия (§2). `type` — свободная
 *  строка, не enum. Остальные типы (`NEXT_BUTTON` — подпись CTA «Далее» в плеере,
 *  `REF_LINK` — партнёрский учёт) студия не показывает, но и не портит: они
 *  переезжают в PUT как есть. */
const LINK_BUTTON_TYPE = 'LINK'
const NEXT_BUTTON_TYPE = 'NEXT_BUTTON'

/** Ссылка ли это. Пустой тип и любой регистр — ссылка (кнопки не из студии бывают
 *  без `type` или с «link»); всё остальное — чужая кнопка, скрытая от формы и лимита. */
export const isLinkButton = (button: BlockButton): boolean => {
  const type = button.type?.trim().toUpperCase()
  return !type || type === LINK_BUTTON_TYPE
}

/** Как читается кнопка, которой автор не дал названия — и в форме, и в превью. */
export const DEFAULT_BUTTON_TITLE = 'Кнопка'

/** Подпись-заготовка новой кнопки: на английской версии русское слово ученик не должен
 *  увидеть, если автор забыл его заменить. Превью по-прежнему падает на `DEFAULT_BUTTON_TITLE`. */
const PLACEHOLDER_TITLE: Record<ContentLang, string> = { ru: DEFAULT_BUTTON_TITLE, en: 'Button' }

export const BUTTON_LIMITS = { TITLE: 255, LINK: 1000 } as const

/** Сколько кнопок можно завести НА ОДИН ЯЗЫК у блока этого типа. У блока картинки
 *  (`info`) заказчик оставил одну (бэк-лог, п.20b), у дизайн-блока тоже одну (решение
 *  Петра, перекрыло лимит 3 из спека). Лимит гасит только «Добавить»: старые дизайн-блоки
 *  с тремя ссылками не обрезаются, видны и правятся (счётчик честно покажет 3/1).
 *  Единственный источник порога: его читают и форма, и всё, что решает, можно ли
 *  добавить ещё одну. Второй копии не заводить. У остальных типов студия кнопок-ссылок не
 *  заводит — 0. */
export const buttonLimitFor = (type: BlockType): number =>
  type === 'info' || type === 'design' ? 1 : 0

/** Язык кнопки. `lang` — свободная строка (§2), и в системе встречается верхний
 *  регистр («EN» у academy-frontend), поэтому сравниваем без учёта регистра: иначе
 *  английская кнопка из старых данных молча попадала бы в русский список.
 *  Пустой `lang` (кнопки не из студии) считаем базовым — иначе они пропали бы из
 *  формы совсем и уехали бы на бек нетронутыми. */
const langOf = (button: BlockButton): ContentLang =>
  button.lang?.trim().toLowerCase() === 'en' ? 'en' : 'ru'

/** На запись отдаём ВЕРХНИЙ регистр — как договорились с плеером, а НЕ потому,
 *  что нижний там не сработает: живой плеер (academy-frontend,
 *  `.../NextBlockButton/blockButtons.ts`) сравнивает язык через
 *  `lang?.trim().toLowerCase() === 'en'`, то есть регистр ему безразличен.
 *  Само поле — свободная строка, бек его не читает. */
const writeLang = (button: BlockButton): string => langOf(button).toUpperCase()

/** Пустое значение — всегда `null`, а не `''`: на публикации бек одинаково ругается на
 *  оба (§7.2), но хранить два способа сказать «пусто» в одном поле незачем. */
const trimmed = (value: string | null): string | null => value?.trim() || null

export const newLinkButton = (lang: ContentLang): BlockButton => ({
  type: LINK_BUTTON_TYPE,
  lang: lang.toUpperCase(),
  title: PLACEHOLDER_TITLE[lang],
  link: null,
  slug: null,
  order: 0,
})

export const buttonsForLang = (
  buttons: BlockButton[] | undefined,
  lang: ContentLang,
): BlockButton[] => (buttons ?? []).filter((b) => langOf(b) === lang && isLinkButton(b))

/** Правки ссылок текущего языка + нетронутое остальное: скрытые кнопки этого же языка
 *  (не ссылки) и чужой язык целиком. Базовый язык идёт первым, чтобы глобальный
 *  `order` (см. normalizeButtons) не перемешивал языки между собой; скрытые кнопки
 *  стоят сразу за ссылками своего языка, их порядок сохраняется. */
export const mergeButtonsForLang = (
  buttons: BlockButton[] | undefined,
  lang: ContentLang,
  next: BlockButton[],
): BlockButton[] => {
  const all = buttons ?? []
  const hidden = all.filter((b) => langOf(b) === lang && !isLinkButton(b))
  const other = all.filter((b) => langOf(b) !== lang)
  return lang === 'ru' ? [...next, ...hidden, ...other] : [...other, ...next, ...hidden]
}

const isNextButton = (button: BlockButton): boolean =>
  button.type?.trim().toUpperCase() === NEXT_BUTTON_TYPE

/** `NEXT_BUTTON` — за все остальные кнопки СВОЕГО языка. TMA сортирует кнопки языка по
 *  `order` и ставит «Далее» последним только при РАВНОМ `order`; на проде у части групп
 *  `order` пуст и «Далее» лежит раньше ссылки, а `normalizeButtons` разводит `order` по
 *  позиции — без этого любое сохранение молча поднимало бы «Далее» над ссылкой.
 *  Кнопка меняет место только внутри мест своего языка: язык остаётся на тех же
 *  позициях, порядок остальных кнопок и самих `NEXT_BUTTON` сохраняется. */
const nextButtonsLast = (buttons: BlockButton[]): BlockButton[] => {
  const result = [...buttons]
  for (const lang of ['ru', 'en'] as const) {
    const slots = buttons.flatMap((b, i) => (langOf(b) === lang ? [i] : []))
    const own = slots.map((i) => buttons[i])
    const ordered = [...own.filter((b) => !isNextButton(b)), ...own.filter(isNextButton)]
    slots.forEach((slot, i) => {
      result[slot] = ordered[i]
    })
  }
  return result
}

/** Write-форма списка: `order` проставлен явно (§5 — на чтении сортировка идёт по
 *  нему, а не по позиции в массиве), серверные поля сняты (`id` пересоздаётся на
 *  каждом сохранении, §5). Ссылка пишется как `LINK` с нулевым `slug`. Чужой тип
 *  (`NEXT_BUTTON`, `REF_LINK`) уходит как есть: подпись CTA и партнёрский `slug`
 *  студия переписывать не вправе. Поля перечислены явно, не спредом — иначе уедут
 *  `id` и прочее из ответа сервера. `color` не шлём совсем. */
export const normalizeButtons = (buttons: BlockButton[] | undefined): BlockButton[] =>
  nextButtonsLast(buttons ?? []).map((b, index) =>
    isLinkButton(b)
      ? {
          type: LINK_BUTTON_TYPE,
          lang: writeLang(b),
          title: trimmed(b.title),
          link: trimmed(b.link),
          slug: null,
          order: index,
        }
      : { type: b.type, lang: b.lang, title: b.title, link: b.link, slug: b.slug, order: index },
  )

/** Ссылку бек не валидирует вообще (§2) — ни схему, ни формат. Пускаем только https:
 *  `javascript:`/`data:` не должны доехать ни до превью, ни до плеера. */
export const isSafeButtonLink = (link: string | null): boolean => {
  const value = trimmed(link)
  if (!value) return true /* пустая ссылка допустима: публикацию она не блокирует (§7.2) */
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}
